import React from 'react';
import {
  ListBase,
  Datagrid,
  DateField,
  FunctionField,
  WrapperField,
  SelectField,
  useRecordContext,
  ReferenceField,
} from 'react-admin';
import {
  Chip,
  Card,
  ChipOwnProps,
  CardHeader,
  Divider,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';

import reportTypes, { SelectableReportTypes, reportTypesDisplayChoices, reportNames } from '../../constants/reportTypes';
import reportStatuses, { ReportStatuses, reportStatusesLabels } from '../../constants/reportStatuses';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import DownloadReports from '../../customFields/DownloadReports';
import Pagination from '../layout/Pagination';
import NoResults from '../layout/NoResults';
import { IReport } from '../../utilities/schemas/report';
import { categoryNames } from './features/reportCategoryFeatures';
import { ReportCategories } from '../../constants/reportCategories';
import useDialogStatus from '../../hooks/useDialogStatus';
import { reportErrorCodeDescriptions } from '../../constants/reportErrorCodes';
import { ViewJSONDialog } from '../../customFields/ViewJSON';
import resourceNames from '../../constants/resourceNames';

const statusColorMap: Record<ReportStatuses, ChipOwnProps['color']> = {
  [reportStatuses.LOADING]: 'warning',
  [reportStatuses.READY]: 'success',
  [reportStatuses.FAILED]: 'error',
};

const excludedReportTypes = [reportTypes.FIPO, reportTypes.ENTITY_REPORT, reportTypes.CASE_PDF];

const UNKNOWN_FAILURE_DESCRIPTION = 'The report could not be generated. Please retry or contact Pingwire support.';

const getMappedErrorDescription = (errorCode?: string) => {
  if (!errorCode || !(errorCode in reportErrorCodeDescriptions)) return undefined;

  return reportErrorCodeDescriptions[errorCode as keyof typeof reportErrorCodeDescriptions];
};

const StatusField: React.FC<{ label?: string }> = () => {
  const record = useRecordContext<IReport>();
  const { open, openDialog, closeDialog } = useDialogStatus();

  if (!record) return null;

  if (record.status !== reportStatuses.FAILED) {
    return (
      <Chip
        label={reportStatusesLabels[record.status]}
        color={statusColorMap[record.status]}
      />
    );
  }

  const mappedErrorDescription = getMappedErrorDescription(record.errorCode);

  return (
    <>
      <Chip
        label={reportStatusesLabels[record.status]}
        color={statusColorMap[record.status]}
        clickable
        onClick={openDialog}
      />
      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Report generation failed</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography>
              {mappedErrorDescription ?? UNKNOWN_FAILURE_DESCRIPTION}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

const InputParametersField: React.FC<{ label?: string }> = () => {
  const record = useRecordContext<IReport>();

  if (!record?.reportInput) return '-';

  return (
    <ViewJSONDialog
      title="Input parameters"
      source="reportInput"
    />
  );
};

export const ReportListDatagrid = () => (
  <>
    <Datagrid
      sx={boldDataGridStyle}
      bulkActionButtons={false}
      empty={<NoResults variant="h6" />}
    >
      <DateField source="createdAt" showTime label="Created At" />
      <SelectField sortable={false} choices={reportTypesDisplayChoices} source="type" />
      <StatusField label="Status" />
      <ReferenceField
        sortable={false}
        label="Generated by"
        source="generatedBy"
        reference={resourceNames.MEMBERS}
        link={false}
        emptyText="-"
      >
        <FunctionField
          render={(member: { firstName: string; lastName: string }) => `${member.firstName} ${member.lastName}`}
        />
      </ReferenceField>
      <InputParametersField label="Input parameters" />
      <WrapperField label="Files">
        <DownloadReports />
      </WrapperField>
    </Datagrid>
    <Pagination rowsPerPageOptions={[10, 25, 50]} />
  </>
);

const ReportHistoryTable = ({
  selectedCategoryId,
  selectedReportType,
  allowedCategoryTypes,
}: {
  selectedCategoryId: ReportCategories | null;
  selectedReportType: SelectableReportTypes | null;
  allowedCategoryTypes: SelectableReportTypes[];
}) => {
  const title = (() => {
    if (selectedReportType) {
      const name = reportNames[selectedReportType];
      return `Previously generated: ${name}`;
    }
    if (selectedCategoryId) return `Previously generated in category: ${categoryNames[selectedCategoryId]}`;
    return 'Previously generated reports';
  })();

  const typesIncluded = (() => {
    if (selectedReportType) return [selectedReportType];
    if (selectedCategoryId && allowedCategoryTypes.length > 0) return allowedCategoryTypes;
    return undefined;
  })();

  const filter = {
    excludeType: excludedReportTypes,
    ...(typesIncluded ? { type: typesIncluded } : {}),
  };

  return (
    <ListBase
      storeKey={false}
      disableSyncWithLocation
      resource="reports"
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      filter={filter}
      queryOptions={{ refetchInterval: 5000 }}
    >
      <Card variant="outlined">
        <CardHeader title={title} />
        <Divider />
        <ReportListDatagrid />
      </Card>
    </ListBase>
  );
};

export default ReportHistoryTable;
